import React, { useRef, useState, useCallback } from "react";
import { ArrowLeftRight } from "lucide-react";

/**
 * Draggable Before/After comparison slider.
 * Shows the "before" image underneath and reveals a resizable left-to-right
 * portion of the "after" image on top.
 */
export default function BeforeAfterSlider({
  beforeSrc,
  afterSrc,
  beforeLabel = "ÎNAINTE",
  afterLabel = "DUPĂ",
  className = "",
}) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50); // percent 0–100
  const [dragging, setDragging] = useState(false);

  const setFromEvent = useCallback((clientX) => {
    if (!containerRef.current) return;
    const r = containerRef.current.getBoundingClientRect();
    const p = ((clientX - r.left) / r.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  const onPointerDown = (e) => {
    setDragging(true);
    setFromEvent(e.clientX);
  };
  const onPointerMove = (e) => {
    if (dragging) setFromEvent(e.clientX);
  };
  const onPointerUp = () => setDragging(false);

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden rounded-lg border border-white/10 bg-black select-none ${className}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      data-testid="before-after-slider"
    >
      {/* After image (the good one) fills the container as the base layer */}
      <img
        src={afterSrc}
        alt="after"
        className="block w-full h-auto pointer-events-none"
        draggable={false}
      />
      {/* Before image clipped from the right side — revealed on the LEFT as user drags */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={beforeSrc}
          alt="before"
          className="block w-full h-auto"
          draggable={false}
        />
      </div>

      {/* Labels */}
      <div className="absolute top-3 left-3 px-2 py-1 rounded bg-black/70 text-white text-[10px] font-bold tracking-[0.2em] backdrop-blur-sm">
        {beforeLabel}
      </div>
      <div className="absolute top-3 right-3 px-2 py-1 rounded bg-orange-500 text-white text-[10px] font-bold tracking-[0.2em]">
        {afterLabel}
      </div>

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_12px_rgba(255,255,255,0.5)]"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
      />
      {/* Handle */}
      <div
        className="absolute top-1/2 w-12 h-12 rounded-full bg-orange-500 shadow-[0_0_20px_rgba(238,116,64,0.6)] flex items-center justify-center text-white cursor-ew-resize hover:scale-110 transition-transform"
        style={{
          left: `${pos}%`,
          transform: "translate(-50%, -50%)",
        }}
      >
        <ArrowLeftRight size={18} strokeWidth={2.5} />
      </div>
    </div>
  );
}
